/**
 * 74.12 step 2 — biomes by the climate (74.3 step 1; DECISIONS 368 order, 363's pass; ARCHITECTURE §5.46 step 2, §5.39 (2)).
 *
 * Today a biome is the strongest of the archetype's noise fields (core/forest.ts forestBiomeAt): the woods land in random
 * blobs, a fir stand as readily on a hot lowland as on a cold height. With `biomeField` on, the field's *inputs* change,
 * not the biomes: the climate cell under a point (core/climate.ts — wet or dry × cold, temperate or hot × low or high,
 * from the spin axis's latitude, the wet mask with the lee, the elevation band and the air) reweights the archetype's
 * channels by each biome's **affinity** for that cell, so the fir stands take the cold heights, the broadleaf the wet
 * temperate lowlands, the heath the high dry ground, the scrub and savanna the hot and dry. The noise still shapes the
 * blobs; the climate says which biome wins where.
 *
 * The weights are renormalised per cell so the best-fitting biome keeps the archetype's full weight: the wooded share of
 * the ground stays the noise's (the field adds no stands, §5.39 (2)); only the kind moves. A cell no biome of the
 * archetype fits at all (every affinity 0) is open ground.
 *
 * The tables are data (a coherence pass: lane H's families by biome stay theirs, core/biomeDressing.ts). Pure.
 */
import type { BiomeId, FloraType } from '../types';
import type { ClimateCellId } from './climate';
import { CLIMATE_CELLS } from './climate';

/** A biome's affinity for each side of the climate's three axes, 0–1 (the product is its affinity for a cell). */
export interface ClimateAffinity { wet: number; dry: number; cold: number; temperate: number; hot: number; low: number; high: number }

export const BIOME_CLIMATE: Record<BiomeId, ClimateAffinity> = {
  // the temperate wet lowland wood: thins in the cold and on the heights, a little dry tolerance
  broadleaf:    { wet: 1,   dry: 0.3, cold: 0.3, temperate: 1,   hot: 0.6, low: 1,   high: 0.4 },
  // the cold wood: the heights and the wet cold; nothing hot
  firStand:     { wet: 1,   dry: 0.5, cold: 1,   temperate: 0.7, hot: 0.1, low: 0.8, high: 1 },
  // windswept high ground, dry or wet, cool
  heath:        { wet: 0.6, dry: 1,   cold: 0.8, temperate: 1,   hot: 0.4, low: 0.3, high: 1 },
  // hot and dry lowland
  savanna:      { wet: 0.3, dry: 1,   cold: 0,   temperate: 0.6, hot: 1,   low: 1,   high: 0.5 },
  // the barrens: dry and warm, anywhere
  ashBarrens:   { wet: 0.5, dry: 1,   cold: 0.4, temperate: 0.8, hot: 1,   low: 1,   high: 0.8 },
  // the lantern grove (DECISIONS 383, the owner: luminescent flora works on snowy worlds, too much elsewhere is gaudy): cold and
  // wet first, an occasional accent in the temperate, rare in the heat; its lantern firs thin outside the cold (`GLOW_TREES`)
  lanternGrove: { wet: 1,   dry: 0.5, cold: 1,   temperate: 0.4, hot: 0.15, low: 1,   high: 0.6 },
  // the birch glade: cool and wet, low
  birchGlade:   { wet: 1,   dry: 0.5, cold: 1,   temperate: 0.9, hot: 0.2, low: 1,   high: 0.5 },
  // the burn: anywhere warm or dry
  burn:         { wet: 0.6, dry: 1,   cold: 0.5, temperate: 1,   hot: 1,   low: 0.8, high: 1 },
  // dry scrub: dry, warm, anywhere
  scrub:        { wet: 0.2, dry: 1,   cold: 0.3, temperate: 1,   hot: 1,   low: 1,   high: 0.8 },
  // the hollow wood: warm, low, wet or dry
  hollowWood:   { wet: 0.8, dry: 0.7, cold: 0.3, temperate: 1,   hot: 1,   low: 1,   high: 0.4 },
};

/**
 * 383, the glowing trees as a kind: outside a cold cell, a stand's draw gives each glowing tree kind its weight × this (the
 * lantern firs of the lantern grove and of the hollow wood alike); in a cold cell the mix is the biome's own. A forced biome's
 * stands (382, the Night forest's grove) keep their mix everywhere.
 */
export const GLOW_TREES: Partial<Record<FloraType, number>> = { lanternFir: 0.25 };

/** A biome's tree mix under a climate cell: the glowing kinds thinned outside the cold (the list copied only when it changes). */
export function climateTrees(trees: Array<[FloraType, number]>, cell: ClimateCellId): Array<[FloraType, number]> {
  if (cellSides(cell).warmth === 'cold' || !trees.some(([t]) => GLOW_TREES[t] !== undefined)) return trees;
  return trees.map(([t, w]) => [t, w * (GLOW_TREES[t] ?? 1)] as [FloraType, number]);
}

/** A cell's three sides. */
export function cellSides(cell: ClimateCellId): { wet: boolean; warmth: 'cold' | 'temperate' | 'hot'; high: boolean } {
  return { wet: cell.startsWith('wet'), warmth: cell.includes('Cold') ? 'cold' : cell.includes('Hot') ? 'hot' : 'temperate', high: cell.endsWith('High') };
}

/** A biome's affinity for a climate cell: the product of its three sides, 0–1. */
export function biomeAffinity(id: BiomeId, cell: ClimateCellId): number {
  const a = BIOME_CLIMATE[id], s = cellSides(cell);
  return (s.wet ? a.wet : a.dry) * a[s.warmth] * (s.high ? a.high : a.low);
}

/**
 * The battle site's choices under a climate cell (core/biomes.ts dressBattleSite, behind `biomeField`): each biome's
 * archetype weight × its affinity for the cell at the site's centre, the biomes nothing fits dropped — the same draws, so the
 * same count of them from the seed; all unfit (never, for a real archetype), the list as it was.
 */
export function siteChoices(list: ReadonlyArray<readonly [BiomeId, number]>, cell: ClimateCellId): Array<[BiomeId, number]> {
  const out = list.map(([id, w]) => [id, w * biomeAffinity(id, cell)] as [BiomeId, number]).filter(([, w]) => w > 0);
  return out.length ? out : list.map(([id, w]) => [id, w] as [BiomeId, number]);
}

/**
 * The archetype's channel weights per climate cell: each biome's archetype weight × its affinity, renormalised so the
 * best-fitting biome in a cell keeps 1 (the forest's `w / top` — the wooded share holds); a cell nothing fits is all 0.
 */
export function climateWeights(list: ReadonlyArray<readonly [BiomeId, number]>): Record<ClimateCellId, number[]> {
  const out = {} as Record<ClimateCellId, number[]>;
  for (const cell of CLIMATE_CELLS) {
    const raw = list.map(([id, w]) => w * biomeAffinity(id, cell));
    const top = Math.max(0, ...raw);
    out[cell] = top > 0 ? raw.map(r => r / top) : raw.map(() => 0);
  }
  return out;
}
